// Growem, sesion movible: el dia de plan EFECTIVO de una fecha y las reglas para cambiarlo.
//
// POR QUE ESTE MODULO EXISTE (y por que aqui):
// hasta hoy cada pantalla derivaba la rutina del dia con planIndexForDow(dow(fecha)), o sea el dia
// de la semana mandaba siempre. Sebastian necesita mover una rutina (hacer el martes el miercoles),
// asi que la fuente de verdad pasa a ser el campo ALMACENADO day_state.plan_day_index (decision D1),
// con el dia de la semana como unico respaldo cuando no hay dato. Para que no haya dos formulas
// distintas conviviendo en el repo, esa formula vive UNA sola vez, aqui (planIdxOf).
//
// POR QUE EL CAMPO ALMACENADO GANA (D1): la rutina que se hizo un dia es un HECHO de ese dia, no una
// consecuencia del calendario. Las claves de cada serie ya llevan el indice dentro ("1-0-2" = rutina
// 1, ejercicio 0, serie 2), asi que si la pantalla derivara el indice del dia de la semana, un dia
// movido mostraria una rutina cuyas claves no casan con lo guardado: series en blanco, historial
// vacio y el coach describiendo otra sesion. Guardar el indice deja el dia coherente para siempre.
//
// POR QUE ESTO NO PUEDE ROMPER LO YA REGISTRADO: en las 19 filas reales de produccion (fixture
// congelada en __fixtures__/seb-days-20260812.ts) el indice guardado coincide SIEMPRE con el dia de
// la semana, o sea planIdxOf devuelve exactamente lo mismo que la formula vieja en cada una de ellas.
// El cambio es, sobre los datos existentes, la funcion identidad; solo abre la puerta a lo nuevo.
//
// Ubicacion (conflicto CR1, ya resuelto y cerrado):
//  - no va en rules.ts porque rules.ts debe quedar sin diff en este milestone (invariante de
//    rollback: si algo sale mal, la racha y el conteo de series no estan tocados);
//  - no va en dates.ts porque dates.ts es aritmetica de calendario pura y no conoce el modelo
//    (no importa DayState en ninguna linea); meterle el modelo le cambiaria el rol.
// Este modulo si importa el modelo (types), el calendario (dates), el plan (data/plan) y el conteo
// de series (rules). No hay ciclo: rules.ts nunca importa planDay.ts.

import type { DayState } from '../types'
import { dow, addDays, planIndexForDow } from './dates'
import { planForDate, DOWS } from '../data/plan'
import { dayCounts } from './rules'

/** Resultado de intentar cambiar la rutina de un dia. */
export type PlanDayDecision =
  | { ok: true; changed: boolean }
  | { ok: false; reason: 'rango' | 'series' }

/** Estado de cada dia de plan (0..5) dentro de la semana en curso. */
export type PlanDayWeekStatus = 'hecha' | 'pendiente' | 'hoy' | 'porVenir'

/** Ultimo indice de plan valido: el plan siempre tiene 6 dias (0 = lunes .. 5 = sabado). */
const MAX_IDX = 5

/** Clave de serie: "rutina-ejercicio-serie" y, en superseries, "rutina-ejercicio-ronda-hijo". */
export const SET_KEY = /^\d+-\d+-\d+(-\d+)?$/

/**
 * Indice de plan EFECTIVO del dia.
 *
 * Comparacion contra null y NUNCA `||`: el indice 0 es el lunes y es falsy, asi que con `||` un lunes
 * movido a otro dia caeria al respaldo del calendario y el override se perderia en silencio. Ojo con
 * la otra cara: el campo es `number | null`, asi que un null GUARDADO es indistinguible de "no hay
 * dato" y tambien se re-deriva; por eso planDayDecision no deja escribir null en un dia de plan.
 *
 * RANGO (SF2): un valor fuera de 0..MAX_IDX (o no entero) tambien cae al respaldo del calendario, con
 * el mismo criterio con el que planDayWeekday degrada a null. La columna day_state.plan_day_index es
 * `int` sin CHECK, o sea el rango solo lo garantiza el cliente; sin esta guardia el modulo se
 * contradecia consigo mismo: planDayLabel decia "descanso" mientras la pantalla hacia
 * `planForDate(fecha)[idx].kick` sobre un `undefined` y se caia a pantalla en blanco. Degradar al
 * calendario (y no a null) mantiene el dia como dia de rutina, que es lo que el usuario espera de un
 * miercoles. Hoy ningun escritor puede producirlo (planDayDecision filtra 0..5 y el seed escribe
 * 0..5), asi que esto es cinturon y tirantes para el dato que ya vive en la base.
 */
export function planIdxOf(d: DayState): number | null {
  const v = d.planDayIndex
  if (v == null || !Number.isInteger(v) || v < 0 || v > MAX_IDX) return planIndexForDow(dow(d.day))
  return v
}

/** Si la rutina de ese dia no es la que le toca por calendario (rutina movida). */
export function isMovedSession(d: DayState): boolean {
  return planIdxOf(d) !== planIndexForDow(dow(d.day))
}

/**
 * Nombre del dia de la semana al que pertenece una rutina. Solo copy, nunca logica.
 * DOWS empieza en domingo, por eso el indice de plan (0 = lunes) se traduce con idx + 1.
 * Fuera de rango devuelve null en vez de un `undefined` disfrazado de string: si algun dia entra un
 * indice corrupto, es mejor que la interfaz no diga nada a que invente un dia.
 */
export function planDayWeekday(idx: number | null): string | null {
  if (idx == null || idx < 0 || idx > MAX_IDX) return null
  return DOWS[idx + 1]
}

/**
 * Etiqueta corta de una rutina para copy e historial: "Pull A (martes)" o "descanso".
 * El nombre completo del plan es "Pull A · Espalda, posterior y biceps"; en un boton o en el popup
 * de deshacer solo cabe (y solo sirve) la parte corta. El plan se resuelve POR FECHA porque las
 * rutinas cambiaron el 2026-07-28 y un dia antiguo debe seguir llamandose como se llamaba entonces.
 */
export function planDayLabel(dayIso: string, idx: number | null): string {
  const dia = planDayWeekday(idx)
  if (idx == null || dia == null) return 'descanso'
  const rutina = planForDate(dayIso)[idx]
  if (!rutina) return 'descanso'
  return `${rutina.name.split(/\s*[·:]\s+/)[0]} (${dia})`
}

/**
 * Si el dia ya tiene datos de ENTRENAMIENTO: alguna serie marcada, o algun numero escrito (peso,
 * repeticiones o RIR) aunque no se haya marcado.
 *
 * Que NO cuenta y por que:
 *  - `at` y `rest` (hora y descanso) son rastro del cronometro, no trabajo hecho: el cronometro
 *    puede haber corrido sin que se hiciera una sola serie, y bloquear por eso seria injusto.
 *  - `prep-*` y `post-*` (calentamiento y cierre) no son series registradas: son pasos previos, y
 *    perderlos al cambiar de rutina no rompe ningun progreso contado, asi que no tienen por que
 *    impedir el cambio. OJO, y esto se documentaba al reves hasta la auditoria de Fase 4: NO son
 *    "del dia". Las listas son POR RUTINA (prepForDate(fecha)[rutina]) y las claves son POSICIONALES
 *    (`prep-2` = el tercer paso de ESA rutina), asi que al cambiar de rutina la misma clave pasa a
 *    nombrar otro ejercicio. Por eso no bloquean el cambio pero SI se borran al cambiar: ver
 *    applyPlanDay.
 *  - `w{rutina}-{ejercicio}` (pesos objetivo) no empieza por digito y queda fuera por construccion.
 * Se barren claves de 3 tramos (serie normal) Y de 4 (superserie): las de 4 son el hueco clasico
 * que se olvida y por el que se colaria un dia con trabajo real hecho.
 */
export function dayHasSessionData(d?: DayState): boolean {
  if (!d) return false
  for (const k of Object.keys(d.done)) {
    if (SET_KEY.test(k)) return true
  }
  for (const [k, v] of Object.entries(d.log)) {
    if (!SET_KEY.test(k) || !v) continue
    if (v.w != null || v.r != null || v.rir != null) return true
  }
  return false
}

/**
 * Decide si se puede cambiar la rutina de un dia y si el cambio es real. En este orden:
 *
 * 1) RANGO. Solo 0..5 enteros. Y null SOLO en domingo: en un dia de plan, guardar null no dejaria el
 *    dia en descanso (planIdxOf lo volveria a derivar del calendario), asi que el usuario veria que
 *    "no paso nada" mientras la base guarda un cambio: un cambio fantasma. El descanso deliberado
 *    entre semana esta fuera de alcance por decision de producto (D1), no por descuido.
 * 2) NO-OP contra el valor ALMACENADO, no contra el efectivo. Va antes del bloqueo por series a
 *    proposito: volver a elegir la misma rutina nunca puede dar error, ni con la sesion empezada.
 *    Que significa "almacenado" en la practica (corregido en la auditoria de Fase 4, antes este
 *    parrafo prometia algo que el store no cumple): mutateDay siembra el dia que no existe con
 *    `defaultDay(day, planIndexForDow(dow(day)))` (store.tsx:256), asi que un dia SIN FILA llega aqui
 *    con el indice del calendario ya puesto y elegir "la que toca hoy" es un no-op de verdad: no se
 *    persiste ni se apila deshacer, que es exactamente lo deseado (no hay nada que clavar, el dia
 *    todavia no existe en la base). El unico caso en que se escribe el indice explicito es una fila
 *    ya cargada de la base con plan_day_index NULL (filas anteriores a la migracion), y ahi si deja
 *    la rutina clavada para que sobreviva a recargar o a que cambie el calendario.
 * 3) BLOQUEO por series. Misma regla que las variantes de ejercicio: si ya hay trabajo registrado,
 *    cambiar de rutina mezclaria claves de dos rutinas en el mismo dia y el progreso quedaria mal
 *    contado (y sin forma limpia de deshacerlo).
 */
export function planDayDecision(d: DayState, idx: number | null): PlanDayDecision {
  if (idx === null) {
    if (planIndexForDow(dow(d.day)) !== null) return { ok: false, reason: 'rango' }
  } else if (!Number.isInteger(idx) || idx < 0 || idx > MAX_IDX) {
    return { ok: false, reason: 'rango' }
  }
  if (d.planDayIndex === idx) return { ok: true, changed: false }
  if (dayHasSessionData(d)) return { ok: false, reason: 'series' }
  return { ok: true, changed: true }
}

/**
 * Aplica el cambio de rutina. Devuelve EL MISMO objeto cuando no hay cambio (rechazado o no-op).
 * Ese contrato de misma referencia es load-bearing: el store no persiste ni apila una entrada de
 * deshacer cuando el dia sale identico, asi que un intento bloqueado no ensucia el historial ni
 * dispara una escritura a la base.
 *
 * POR QUE ADEMAS SE BORRAN LOS PASOS DE PREPARACION Y CIERRE (MF0 de la auditoria de Fase 4):
 * las claves `prep-{i}` / `post-{i}` son POSICIONALES dentro de la lista de LA RUTINA
 * (prepForDate(fecha)[rutina]), no del dia. Al mover el dia de una rutina a otra, la marca se queda
 * pero cambia de significado: el caso real es el paso 1 de Pull A ("Band pull-aparts x20") que en
 * Push A es "Manguito rotador (hombro izq.)", el calentamiento de seguridad del hombro izquierdo
 * antes de cualquier press. Dejar esa casilla marcada afirma que se hizo un paso que nunca se hizo, y
 * justo en el unico paso cuya omision puede lesionar. Ademas, al pasar a una rutina con menos pasos,
 * las claves sobrantes quedan huerfanas en el dia y viajan a la base. Borrar es seguro para deshacer:
 * el store snapshotea el DIA ENTERO en pushEntry (store.tsx:262), asi que un undo repone las marcas
 * tal cual estaban. Solo corre en el cambio real (dec.changed), asi que el contrato de misma
 * referencia queda intacto.
 */
export function applyPlanDay(d: DayState, idx: number | null): DayState {
  const dec = planDayDecision(d, idx)
  if (!dec.ok || !dec.changed) return d
  const done = { ...d.done }
  for (const k of Object.keys(done)) {
    if (k.startsWith('prep-') || k.startsWith('post-')) delete done[k]
  }
  return { ...d, planDayIndex: idx, done }
}

/**
 * Estado de las seis RUTINAS de la semana en curso (lunes a sabado), indexado por dia de plan.
 *
 * La semana ancla en el lunes anterior, y el domingo pertenece a la semana que TERMINA (por eso
 * resta 6 y no 0): el domingo es el descanso de la semana que acaba de pasar, no la vispera de la
 * siguiente. "hecha" es hubo series (dayCounts), no "existe la fila": hay filas reales creadas solo
 * para dejar una nota ("no alcance a ir al gym") y contarlas como hechas seria mentir.
 *
 * ATRIBUCION POR INDICE EFECTIVO (MF3 de la auditoria de Fase 4). Antes esta funcion respondia por
 * RANURA DE CALENDARIO (el dia lunes+i), pero el selector traduce cada estado a un hecho de RUTINA
 * ("ya la hiciste" / "no la hiciste"), y con una sesion movida la posicion del calendario ya no
 * corresponde a la rutina que se hizo: la falsedad no era teorica, es el caso real que este milestone
 * habilita (Pull A hecha el miercoles dejaba a Pull A rotulada "no la hiciste" y a Legs A, que nadie
 * hizo, rotulada "ya la hiciste"). Ahora cada dia atribuye su trabajo a la rutina que REALMENTE se
 * hizo, via planIdxOf.
 *
 * Prioridad de los estados, y por que en ese orden:
 *  1) "hoy" gana en la ranura del dia de calendario de hoy. Es el ancla que el selector necesita
 *     ("la que toca hoy") y es un hecho de calendario, siempre verdadero. Sin esta prioridad, un dia
 *     movido dejaria al usuario sin saber cual es la rutina que le corresponde por calendario.
 *  2) "hecha" para toda rutina que algun dia NO FUTURO de la semana registro (incluido hoy mismo:
 *     si hoy ya llevas series de Pull A movida, Pull A esta hecha aunque su martes siga vacio, y esa
 *     es justo la mentira que MF3 venia a matar).
 *  3) "pendiente" solo si su dia de calendario ya paso y ningun dia registro esa rutina.
 *  4) "porVenir" el resto (su dia todavia no llega y nadie la adelanto).
 *
 * Limitacion aceptada que NO cambia: la racha y el mapa del mes siguen siendo por dia de calendario
 * (el dia origen no se sana). Aqui la unidad es la rutina porque el consumidor pregunta por rutinas;
 * alli la unidad es el dia porque lo que se cuenta son dias entrenados.
 */
export function weekPlanStatus(days: Record<string, DayState>, todayIso: string): PlanDayWeekStatus[] {
  const wd = dow(todayIso)
  const lunes = addDays(todayIso, -(wd === 0 ? 6 : wd - 1))
  // Paso 1: que rutinas registraron trabajo esta semana, sin importar en que dia se hizo.
  const hechas = new Set<number>()
  for (let i = 0; i <= MAX_IDX; i++) {
    const iso = addDays(lunes, i)
    // Las fechas ISO se comparan como texto sin ambiguedad (mismo formato y misma longitud).
    if (iso > todayIso) continue           // el futuro no puede tener trabajo hecho
    const d = days[iso]
    if (!d || !dayCounts(d)) continue
    const idx = planIdxOf(d)               // ya viene clampeado a 0..MAX_IDX o null (ver planIdxOf)
    if (idx != null) hechas.add(idx)
  }
  // Paso 2: rotular cada rutina con su ranura de calendario como respaldo.
  const out: PlanDayWeekStatus[] = []
  for (let i = 0; i <= MAX_IDX; i++) {
    const iso = addDays(lunes, i)
    if (iso === todayIso) out.push('hoy')
    else if (hechas.has(i)) out.push('hecha')
    else if (iso < todayIso) out.push('pendiente')
    else out.push('porVenir')
  }
  return out
}

/**
 * La rutina de ayer que se puede recuperar hoy, o null cuando no hay nada que ofrecer.
 *
 * Se ofrece solo si TODO esto se cumple: ayer era dia de rutina, ayer no se entreno, hoy todavia no
 * tiene nada registrado (si ya empezaste, cambiar esta bloqueado de todos modos) y hoy no esta ya
 * puesto en esa misma rutina (no tiene sentido ofrecer lo que ya se eligio). Es pura y sin efectos
 * para que la pantalla solo la consuma: la decision de mostrar u ocultar el aviso es de la pantalla.
 *
 * Un dia sin fila cuenta como dia sin entrenar: en esta app la fila solo nace cuando se toca algo.
 *
 * QUE SIGNIFICA CADA CAMPO DEL RESULTADO (SF1 de la auditoria de Fase 4, para que la pantalla no los
 * confunda otra vez): `day` es la FECHA de ayer y es lo unico que puede nombrar el dia del calendario;
 * `idx` es el indice EFECTIVO de esa fecha, o sea la rutina que se perdio, que con un dia movido ya
 * NO es la rutina del dia de la semana de ayer; `label` es esa rutina ya nombrada por el dominio
 * ("Pull A (martes)"). El copy de la pantalla debe sacar el dia del calendario de `day` y la rutina
 * de `label`/`idx`: derivar el dia de la semana a partir de `idx` es exactamente lo que hacia que el
 * chip dijera "Ayer martes..." un miercoles. Se devuelve `label` ya compuesto para que ninguna
 * pantalla tenga que recomponer el nombre por su cuenta.
 */
export function recoverableYesterday(
  days: Record<string, DayState>,
  todayIso: string,
): { day: string; idx: number; label: string } | null {
  const ayer = addDays(todayIso, -1)
  const dAyer = days[ayer]
  const idxAyer = dAyer ? planIdxOf(dAyer) : planIndexForDow(dow(ayer))
  if (idxAyer == null) return null      // ayer no habia rutina que perder (domingo)
  if (dayCounts(dAyer)) return null     // ayer si se entreno
  const dHoy = days[todayIso]
  if (dayHasSessionData(dHoy)) return null
  const idxHoy = dHoy ? planIdxOf(dHoy) : planIndexForDow(dow(todayIso))
  if (idxHoy === idxAyer) return null   // hoy ya esta puesto en esa rutina
  // La etiqueta se resuelve con la fecha de AYER porque el plan cambio el 2026-07-28: la rutina que
  // se perdio debe llamarse como se llamaba ese dia, no como se llame hoy.
  return { day: ayer, idx: idxAyer, label: planDayLabel(ayer, idxAyer) }
}
